import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BusinessEntity, useUpdateBusiness } from "@/hooks/useBusinesses";
import { wizardSchema, WizardValues } from "../wizard/schema";
import { FacilityStep } from "../wizard/FacilityStep";
import { PhotosPanel } from "./PhotosPanel";

interface Props {
  b: BusinessEntity;
}

const seed = (b: BusinessEntity): Partial<WizardValues> => ({
  ownerId: b.ownerId,
  businessName: b.businessName,
  nameEn: b.nameEn,
  nameMl: b.nameMl,
  address: b.address,
  city: b.city,
  pincode: b.pincode ?? "",
  phone: b.phone,
  lat: b.lat != null ? Number(b.lat) : undefined,
  lng: b.lng != null ? Number(b.lng) : undefined,
  careTypes: ((b.careTypes as string[]) ?? []).map((c) =>
    c.toLowerCase(),
  ) as any,
  amenityIds: (b.amenities ?? []).map((a) => a.amenityId),
});

export const FacilityTab = ({ b }: Props) => {
  const form = useForm<WizardValues>({
    resolver: zodResolver(wizardSchema),
    mode: "onBlur",
    defaultValues: seed(b),
  });
  const update = useUpdateBusiness();

  useEffect(() => {
    form.reset(seed(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id]);

  const onSave = form.handleSubmit(async (values) => {
    const patch: Partial<BusinessEntity> = {
      careTypes: values.careTypes as string[],
      amenityIds: values.amenityIds,
    } as any;
    try {
      await update.mutateAsync({ id: b.id, data: patch });
      toast.success("Facility updated");
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    }
  });

  return (
    <div className="space-y-6">
      <FormProvider {...form}>
        <form onSubmit={onSave} className="space-y-4">
          <section className="bg-card rounded-card border border-border p-5 space-y-4">
            <h3 className="font-semibold">Care & amenities</h3>
            <FacilityStep />
          </section>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset(seed(b))}
              disabled={!form.formState.isDirty || update.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!form.formState.isDirty || update.isPending}
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </FormProvider>

      <PhotosPanel b={b} />
    </div>
  );
};
